import { pageMetadata } from "@/data/page-metadata";
import { locationSeoPages } from "@/data/location-seo-pages";

type MetaEntry = { title?: string; name?: string; property?: string; content?: string };
type Head = { meta?: MetaEntry[]; scripts?: { type?: string; children?: string }[] };

export function getPageMetadata(path: string) {
  const location = path.startsWith("/lawyer-seo/") ? locationSeoPages[path.slice("/lawyer-seo/".length)] : undefined;
  if (location) return { title: location.title, description: location.description };
  return pageMetadata[path];
}

// Only supplied pages are affected. KEEP fields retain the existing leaf values.
export function applyPageMetadata<T extends Head>(path: string, head: T): T {
  const override = getPageMetadata(path);
  if (!override) return head;
  const title = override.title ?? head.meta?.find((entry) => entry.title !== undefined)?.title;
  const description = override.description ?? head.meta?.find((entry) => entry.name === "description")?.content;
  const meta = head.meta?.map((entry) => {
    if (entry.title !== undefined && title !== undefined) return { ...entry, title };
    if ((entry.name === "description" || entry.property === "og:description" || entry.name === "twitter:description") && description !== undefined) return { ...entry, content: description };
    if ((entry.property === "og:title" || entry.name === "twitter:title") && title !== undefined) return { ...entry, content: title };
    return entry;
  });
  // Override inherited root Twitter defaults when the leaf had no explicit tag.
  if (meta && title !== undefined && !meta.some((entry) => entry.name === "twitter:title")) meta.push({ name: "twitter:title", content: title });
  if (meta && description !== undefined && !meta.some((entry) => entry.name === "twitter:description")) meta.push({ name: "twitter:description", content: description });
  if (meta && !meta.some((entry) => entry.property === "og:type")) meta.push({ property: "og:type", content: "website" });
  if (meta && !meta.some((entry) => entry.name === "twitter:card")) meta.push({ name: "twitter:card", content: "summary" });
  function alignArticle(value: unknown): unknown {
    if (Array.isArray(value)) return value.map(alignArticle);
    if (value === null || typeof value !== "object") return value;
    const record = value as Record<string, unknown>;
    const types = Array.isArray(record["@type"]) ? record["@type"] : [record["@type"]];
    const result = Object.fromEntries(Object.entries(record).map(([key, item]) => [key, alignArticle(item)]));
    if (types.includes("Article") || types.includes("BlogPosting")) {
      if ("headline" in record && title !== undefined) result.headline = title;
      if ("description" in record && description !== undefined) result.description = description;
    }
    return result;
  }
  const scripts = head.scripts?.map((script) => {
    if (script.type !== "application/ld+json" || !script.children) return script;
    return { ...script, children: JSON.stringify(alignArticle(JSON.parse(script.children))) };
  });
  return { ...head, meta, ...(scripts ? { scripts } : {}) };
}
